import type { PointsBenefitsBreakdown, PointsBenefitsData, PointsBenefitsYearPoints } from '@account/hooks/usePointsBenefits'
import type { AnalyticsGlobalContext } from '../types'
import {
  coerceAnalyticsYyyyMmDd,
  formatAnalyticsYyyyMmDdFromMs,
  inferUseYearBeginMsFromEndMs,
  parseValidThroughDatesFromNote,
} from './sdrAnalyticsDates'

/** Collect `breakdown` objects from membership summary and nested benefits payloads. */
export function collectBreakdownsFromPointsBenefits(data: PointsBenefitsData | null | undefined): PointsBenefitsBreakdown[] {
  if (!data) {
    return []
  }
  const out: PointsBenefitsBreakdown[] = []
  if (data.membershipSummary?.breakdown) {
    out.push(data.membershipSummary.breakdown)
  }
  const walk = (node: unknown): void => {
    if (node == null || typeof node !== 'object') {
      return
    }
    if (Array.isArray(node)) {
      for (const x of node) {
        walk(x)
      }
      return
    }
    const o = node as Record<string, unknown>
    if (o.breakdown && typeof o.breakdown === 'object') {
      out.push(o.breakdown as PointsBenefitsBreakdown)
    }
    for (const v of Object.values(o)) {
      walk(v)
    }
  }
  walk(data.remainingBenefits?.data)
  walk(data.featuredBenefits?.data)
  return out
}

/** Use-year bounds from breakdown "Valid through …" notes when `yearPoints` is empty. */
export function extractFallbackUseYearsFromPointsBenefits(
  data: PointsBenefitsData | null | undefined,
): Pick<AnalyticsGlobalContext, 'current_use_year' | 'end_use_year'> {
  const ends: number[] = []
  for (const b of collectBreakdownsFromPointsBenefits(data)) {
    for (const row of b.details ?? []) {
      const ms = parseValidThroughDatesFromNote(row.note)
      if (ms !== undefined) {
        ends.push(ms)
      }
    }
  }
  if (ends.length === 0) {
    return {}
  }
  const now = Date.now()
  const inWindow = ends.filter((e) => e >= now - 500 * 24 * 60 * 60 * 1000)
  const chosenEnd = inWindow.length > 0 ? Math.min(...inWindow) : Math.max(...ends)
  const endStr = formatAnalyticsYyyyMmDdFromMs(chosenEnd)
  const beginStr = formatAnalyticsYyyyMmDdFromMs(inferUseYearBeginMsFromEndMs(chosenEnd))
  return {
    current_use_year: beginStr,
    end_use_year: endStr,
  }
}

/** Global keys from `GET /pointsBenefits`. Key list: `README.md`. */
export function sdrFieldsFromPointsBenefits(
  data: PointsBenefitsData | null | undefined,
): Partial<AnalyticsGlobalContext> {
  if (!data?.membershipSummary) {
    return {}
  }
  const out: Partial<AnalyticsGlobalContext> = {}
  const yp = yearPointsForAnalytics(data)

  const beginCompact = yp?.useYearBegin ? coerceAnalyticsYyyyMmDd(yp.useYearBegin) : undefined
  if (beginCompact) {
    out.current_use_year = beginCompact
  }
  const endCompact = yp?.useYearEnd ? coerceAnalyticsYyyyMmDd(yp.useYearEnd) : undefined
  if (endCompact) {
    out.end_use_year = endCompact
  }
  const totalOwned = yp?.totalPoints ?? data.membershipSummary.totalPoints
  if (totalOwned) {
    out.points_owned = stripNumberFormatting(totalOwned)
  }

  const hk = housekeepingCreditsFromAllBreakdowns(data)
  if (hk !== undefined) {
    out.house_keeping_credits = hk
  }

  return out
}

function stripNumberFormatting(s: string): string {
  return s.replace(/,/g, '').trim()
}

function yearPointsForAnalytics(data: PointsBenefitsData): PointsBenefitsYearPoints | undefined {
  const byYear = data.membershipSummary?.yearPoints
  if (!byYear || Object.keys(byYear).length === 0) {
    return undefined
  }
  const defaultKey = data.defaultBenefitYear
  if (defaultKey && byYear[defaultKey]) {
    return byYear[defaultKey]
  }
  for (const key of Object.keys(byYear)) {
    const y = byYear[key]
    if (y?.useYearBegin && y?.useYearEnd) {
      return y
    }
  }
  const firstKey = Object.keys(byYear)[0]
  return firstKey ? byYear[firstKey] : undefined
}

function housekeepingCreditsFromAllBreakdowns(data: PointsBenefitsData): string | undefined {
  let best: number | undefined
  for (const b of collectBreakdownsFromPointsBenefits(data)) {
    const hk = housekeepingCreditsFromBreakdown(b)
    if (hk === undefined) {
      continue
    }
    const n = parseInt(stripNumberFormatting(hk), 10)
    if (Number.isNaN(n)) {
      continue
    }
    if (best === undefined || n > best) {
      best = n
    }
  }
  return best === undefined ? undefined : String(best)
}

function housekeepingCreditsFromBreakdown(breakdown: PointsBenefitsBreakdown | undefined): string | undefined {
  if (!breakdown) {
    return undefined
  }
  const totalTitle = breakdown.total?.title ?? ''
  if (/housekeeping/i.test(totalTitle) && breakdown.total?.volume) {
    return stripNumberFormatting(breakdown.total.volume)
  }
  let sum = 0
  let any = false
  for (const row of breakdown.details ?? []) {
    if (row.title && /housekeeping/i.test(row.title) && row.volume != null && row.volume !== '') {
      const n = parseInt(stripNumberFormatting(String(row.volume)), 10)
      if (!Number.isNaN(n)) {
        sum += n
        any = true
      }
    }
  }
  if (any) {
    return String(sum)
  }
  return undefined
}
